import { getApiErrorMessage } from '../services/api';
import React, { useState, useEffect, useCallback } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import eyvoLogo from '../assets/eyvo_logo.png';
import { SortableTableHeader, useSortState, sortData } from '../components/SortableTableHeader';

import EyvoAdminLogin from './EyvoAdmin/components/EyvoAdminLogin';
import TenantUsersPanel from './EyvoAdmin/components/TenantUsersPanel';
import TenantEditModal from './EyvoAdmin/components/TenantEditModal';
import NewTenantModal from './EyvoAdmin/components/NewTenantModal';
import StaffList from './EyvoAdmin/components/StaffList';

import {
  Users, Shield, Building2, BarChart2, CheckCircle, XCircle,
  Edit, AlertTriangle, LogOut, Plus, RefreshCw, Eye, EyeOff, Key, Trash2
} from 'lucide-react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_CLIENT_ID = '844565024014-ap42nanrspqvirdfndk1f68tcadl3mai.apps.googleusercontent.com';

const MODULE_LABELS = {
  requisitions:'Purchase Requisitions', orders:'Purchase Orders', rfq:'RFx Sourcing',
  contracts:'Contract Management', goods_receiving:'Receiving', invoicing:'Invoicing', assets:'Asset Management',
  inventory:'Inventory Control', expenses:'Employee Expenses', supplier_portal:'Supplier Portal', sales_orders:'Sales Order Processing',
  multi_site:'Multi-Site / Multi-Company', ocr:'Invoice OCR', recipe:'Recipe Management',
};
const ALL_MODULE_KEYS = Object.keys(MODULE_LABELS);

const STATUS_STYLES = {
  active:'bg-green-100 text-green-700', trial:'bg-blue-100 text-blue-700',
  suspended:'bg-red-100 text-red-700', cancelled:'bg-gray-100 text-gray-500',
};

function apiWithToken(token) {
  return axios.create({ baseURL: BACKEND_URL, headers: { Authorization: `Bearer ${token}` } });
}

// ── Google-only Login Screen ──────────────────────────────────────────────────

// ── KPI Card ──────────────────────────────────────────────────────────────────
const KPI = ({ label, value, icon: Icon, color }) => (
  <div className="kpi-card">
    <div className="flex items-start justify-between">
      <div><p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold mt-1">{value ?? '—'}</p></div>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="h-4 w-4" /></div>
    </div>
  </div>
);

// ── Tenant Users Panel ────────────────────────────────────────────────────────

// ── Tenant Edit Modal ─────────────────────────────────────────────────────────

// ── New Tenant Modal ──────────────────────────────────────────────────────────

// ── Staff List ────────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// MAIN PORTAL
// ═══════════════════════════════════════════════════════════════════════════
export default function EyvoAdminPortal() {
  const [token, setToken]               = useState(() => localStorage.getItem('eyvo_admin_token') || '');
  const [staff, setStaff]               = useState(null);
  const [activeTab, setActiveTab]       = useState('dashboard');
  const [stats, setStats]               = useState(null);
  const [tenants, setTenants]           = useState([]);
  const [tenantSort, handleTenantSort] = useSortState('company_name', 'asc');
  const [totalTenants, setTotalTenants] = useState(0);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingTenant, setEditingTenant] = useState(null);
  const [newTenantOpen, setNewTenantOpen] = useState(false);
  const [deletingTenant, setDeletingTenant] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [loading, setLoading]           = useState(false);
  const [version, setVersion]           = useState(null);

  const api = apiWithToken(token);

  const handleLogin = (tok, staffInfo) => {
    localStorage.setItem('eyvo_admin_token', tok);
    setToken(tok); setStaff(staffInfo);
  };
  const handleLogout = () => {
    localStorage.removeItem('eyvo_admin_token');
    setToken(''); setStaff(null);
  };

  const loadDashboard = useCallback(async () => {
    try {
      const [statsRes, versionRes] = await Promise.all([
        api.get('/api/eyvo-admin/dashboard'),
        api.get('/api/system/version'),
      ]);
      setStats(statsRes.data);
      setVersion(versionRes.data);
    } catch {}
  }, [token]); // eslint-disable-line

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/eyvo-admin/tenants', { params: { search, status: statusFilter, page_size: 50 } });
      setTenants(r.data.data); setTotalTenants(r.data.total);
    } catch {}
    setLoading(false);
  }, [token, search, statusFilter]); // eslint-disable-line

  useEffect(() => { if (token) { loadDashboard(); loadTenants(); } }, [token, loadDashboard, loadTenants]);

  const quickAction = async (tenantId, action) => {
    try { 
      await api.post(`/api/eyvo-admin/tenants/${tenantId}/${action}`); 
      toast.success(`${action}d`); 
      loadTenants(); 
      loadDashboard(); 
    } catch (e) { 
      toast.error(getApiErrorMessage(e, 'Action failed')); 
    }
  };

  const handleDeleteTenant = async () => {
    if (!deletingTenant) return;
    
    if (deleteConfirmation !== deletingTenant.tenant_id) {
      toast.error(`Please type '${deletingTenant.tenant_id}' exactly to confirm`);
      return;
    }
    
    try {
      const res = await api.delete(`/api/eyvo-admin/tenants/${deletingTenant.tenant_id}`, {
        data: { confirmation_code: deleteConfirmation }
      });
      toast.success(res.data.message);
      setDeletingTenant(null);
      setDeleteConfirmation('');
      loadTenants();
      loadDashboard();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to delete tenant'));
    }
  };

  if (!token) return <EyvoAdminLogin onLogin={handleLogin} />;

  const fmtDate = d => { try { return new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric', timeZone:'America/Los_Angeles'}); } catch { return d||''; } };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="sidebar-nav flex flex-col h-screen sticky top-0 w-56 flex-shrink-0">
        <div className="flex items-center gap-2 px-4 py-4 border-b" style={{borderColor:'hsl(var(--sidebar-border))'}}>
          <img src={eyvoLogo} alt="Eyvo" className="h-8 w-auto flex-shrink-0" style={{filter:'brightness(0) invert(1)'}} />
          <div>
            <p className="text-white text-xs font-bold">Admin Portal</p>
            <p className="text-white/40 text-[10px]">Eyvo Staff</p>
          </div>
        </div>
        {/* Staff avatar from Google */}
        {staff && (
          <div className="px-4 py-3 border-b flex items-center gap-2" style={{borderColor:'hsl(var(--sidebar-border))'}}>
            {staff.google_picture
              ? <img src={staff.google_picture} alt="" className="w-7 h-7 rounded-full flex-shrink-0" />
              : <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold">{staff.name?.[0]}</div>
            }
            <div className="min-w-0">
              <p className="text-white text-xs font-medium truncate">{staff.google_name || staff.name}</p>
              <p className="text-white/40 text-[10px] truncate">{staff.email}</p>
            </div>
          </div>
        )}
        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {[{id:'dashboard',label:'Dashboard',icon:BarChart2},{id:'tenants',label:'Clients',icon:Building2},{id:'staff',label:'Eyvo Staff',icon:Shield}].map(item=>(
            <button key={item.id} onClick={()=>setActiveTab(item.id)}
              className={`sidebar-item w-full text-left ${activeTab===item.id?'active':''}`}>
              <item.icon className="h-4 w-4 flex-shrink-0" /><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="px-4 py-3 border-t" style={{borderColor:'hsl(var(--sidebar-border))'}}>
          <button onClick={handleLogout} className="sidebar-item w-full text-left">
            <LogOut className="h-4 w-4" /><span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 overflow-auto">
        <div className="px-8 py-6">

          {/* Dashboard */}
          {activeTab === 'dashboard' && stats && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold" style={{fontFamily:'Space Grotesk'}}>Overview</h1>
                {version && (
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <span className="font-mono font-semibold">v{version.version}</span>
                    <span>•</span>
                    <span>Deployed {new Date(version.deployed_at).toLocaleString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles'
                    })}</span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI label="Total Clients"    value={stats.total_tenants}         icon={Building2}    color="bg-blue-50 text-blue-600" />
                <KPI label="Active"           value={stats.active}                icon={CheckCircle}  color="bg-green-50 text-green-600" />
                <KPI label="On Trial"         value={stats.trial}                 icon={Key}          color="bg-amber-50 text-amber-600" />
                <KPI label="Licensed Users"   value={stats.total_licensed_users}  icon={Users}        color="bg-purple-50 text-purple-600" />
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="border rounded-xl p-5">
                  <h3 className="font-semibold mb-3">Module Popularity</h3>
                  <div className="space-y-2">
                    {(stats.module_popularity || []).map(([mod,count]) => (
                      <div key={mod} className="flex items-center justify-between">
                        <span className="text-sm">{MODULE_LABELS[mod] || mod}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary rounded-full"
                              style={{width:`${Math.min(100,(count/Math.max(stats.total_tenants,1))*100)}%`}} />
                          </div>
                          <span className="text-xs text-muted-foreground w-5 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border rounded-xl p-5">
                  <h3 className="font-semibold mb-3">Recent Clients</h3>
                  {(stats.recent_tenants || []).map(t => (
                    <div key={t.tenant_id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                      <div><p className="text-sm font-medium">{t.company_name}</p><p className="text-xs text-muted-foreground">{t.tenant_id}</p></div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[t.status]||''}`}>{t.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Tenants */}
          {activeTab === 'tenants' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold" style={{fontFamily:'Space Grotesk'}}>Clients ({totalTenants})</h1>
                <Button size="sm" onClick={() => setNewTenantOpen(true)}><Plus className="h-4 w-4 mr-1" />New Client</Button>
              </div>
              <div className="flex gap-3">
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" className="max-w-xs h-9" />
                <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="h-9 rounded-lg border border-input bg-background px-3 text-sm">
                  <option value="">All Statuses</option>
                  {['active','trial','suspended','cancelled'].map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="rounded-xl border bg-card overflow-hidden">
                {loading ? <p className="py-8 text-center text-muted-foreground text-sm">Loading…</p> : (
                  <table className="data-table w-full">
                    <thead><tr>
                      <SortableTableHeader label="Client Name" field="company_name" sortState={tenantSort} onSort={handleTenantSort} />
                      <SortableTableHeader label="Code" field="company_code" sortState={tenantSort} onSort={handleTenantSort} />
                      <SortableTableHeader label="Phone" field="phone" sortState={tenantSort} onSort={handleTenantSort} />
                      <SortableTableHeader label="City / Country" field="city" sortState={tenantSort} onSort={handleTenantSort} />
                      <SortableTableHeader label="Plan" field="plan" sortState={tenantSort} onSort={handleTenantSort} />
                      <SortableTableHeader label="Status" field="status" sortState={tenantSort} onSort={handleTenantSort} />
                      <SortableTableHeader label="Users" field="user_count" sortState={tenantSort} onSort={handleTenantSort} className="text-right" />
                      <SortableTableHeader label="Created" field="created_at" sortState={tenantSort} onSort={handleTenantSort} />
                      <th className="px-4 py-3"></th>
                    </tr></thead>
                    <tbody>
                      {sortData(tenants, tenantSort.field, tenantSort.dir).map(t => (
                        <tr key={t.tenant_id} className="border-b hover:bg-accent/30 cursor-pointer" onClick={() => setEditingTenant(t)}>
                          <td className="px-4 py-3">
                            <p className="font-semibold text-sm">{t.company_name}</p>
                            <p className="text-xs text-muted-foreground">{t.billing_email}</p>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-primary">{t.tenant_id}</td>
                          <td className="px-4 py-3 text-sm">{t.phone || '—'}</td>
                          <td className="px-4 py-3 text-sm">{[t.company_city, t.company_country].filter(Boolean).join(', ') || '—'}</td>
                          <td className="px-4 py-3"><span className="text-xs capitalize">{t.plan}</span></td>
                          <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[t.status]||''}`}>{t.status}</span></td>
                          <td className="px-4 py-3 text-right text-sm">{t.user_count || 0} / {t.user_license_limit}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(t.created_at)}</td>
                          <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-1">
                              {t.status==='active' && <button onClick={()=>quickAction(t.tenant_id,'suspend')} className="p-1 hover:bg-accent rounded text-destructive" title="Suspend"><XCircle className="h-3.5 w-3.5" /></button>}
                              {t.status!=='active' && <button onClick={()=>quickAction(t.tenant_id,'activate')} className="p-1 hover:bg-accent rounded text-green-600" title="Activate"><CheckCircle className="h-3.5 w-3.5" /></button>}
                              {t.tenant_id !== 'eyvo-demo' && (
                                <button 
                                  onClick={() => setDeletingTenant(t)} 
                                  className="p-1 hover:bg-destructive/10 rounded text-destructive" 
                                  title="Delete Tenant"
                                  data-testid={`delete-tenant-${t.tenant_id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Staff */}
          {activeTab === 'staff' && (
            <div className="space-y-4">
              <h1 className="text-xl font-bold" style={{fontFamily:'Space Grotesk'}}>Eyvo Staff</h1>
              <StaffList token={token} />
            </div>
          )}
        </div>
      </div>

      {/* Delete Tenant Confirmation Modal */}
      {deletingTenant && (
        <Dialog open={true} onOpenChange={() => { setDeletingTenant(null); setDeleteConfirmation(''); }}>
          <DialogContent data-testid="delete-tenant-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Delete Tenant - {deletingTenant.company_name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-destructive">
                  ⚠️ This action is PERMANENT and cannot be undone!
                </p>
                <ul className="text-xs text-muted-foreground space-y-1 ml-4 list-disc">
                  <li>All tenant data will be permanently deleted</li>
                  <li>The database <code className="bg-background px-1 rounded">{deletingTenant.db_name}</code> will be dropped</li>
                  <li>All users, requisitions, suppliers, contracts, and orders will be lost</li>
                  <li>This tenant will be unable to log in</li>
                </ul>
              </div>
              
              {deletingTenant.tenant_id === 'eyvo-demo' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm font-bold text-amber-800">
                    🔒 PROTECTED: eyvo-demo cannot be deleted
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="delete-confirmation">
                  Type <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-sm">{deletingTenant.tenant_id}</code> to confirm deletion:
                </Label>
                <Input
                  id="delete-confirmation"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder={deletingTenant.tenant_id}
                  data-testid="delete-confirmation-input"
                  autoFocus
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => { setDeletingTenant(null); setDeleteConfirmation(''); }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteTenant}
                disabled={deleteConfirmation !== deletingTenant.tenant_id}
                data-testid="confirm-delete-button"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {editingTenant && (
        <TenantEditModal tenant={editingTenant} token={token}
          onClose={()=>setEditingTenant(null)}
          onSaved={()=>{loadTenants();loadDashboard();}}
        />
      )}
      {newTenantOpen && (
        <NewTenantModal token={token}
          onClose={()=>setNewTenantOpen(false)}
          onCreated={()=>{loadTenants();loadDashboard();}}
        />
      )}
    </div>
  );
}
